import React from "react";
import {
  Card,
  Typography,
  Select,
  TreeSelect,
  Input,
  Button,
  Collapse,
  Badge,
  Space,
  Divider,
} from "antd";

const { Text } = Typography;
const { Panel } = Collapse;

const Step3 = ({
  getPairs,
  marketAssignments,
  coreBrandDraftSelections,
  coreBrandDraftName,
  companies,
  skuCompaniesLoading,
  companyBrandsLoading,
  getBrandsTreeData,
  getCompetitorsLoading,
  getCompetitorsOptions,
  addGroup,
  removeGroup,
  applyToAllMarkets,
  setCompany,
  setCompetitors,
  setCoreBrandDraftSelections,
  setCoreBrandDraftName,
  competitorsRequired = false,
}) => {
  const pairs = getPairs();

  // Group pairs by region for better organization
  const groupedPairs = pairs.reduce((acc, pair) => {
    const { regionId, regionName } = pair;
    if (!acc[regionId]) {
      acc[regionId] = {
        regionName,
        markets: [],
      };
    }
    acc[regionId].markets.push(pair);
    return acc;
  }, {});

  // A market is complete if it has a company and at least one core brand group.
  // Creation additionally requires competitors: the SQL that builds a market's
  // performance data assumes they exist, and generates an invalid CASE without
  // them, so a workspace created with none can never be built. Editing an
  // existing market does not require them, so a market configured before this
  // rule stays editable.
  const isMarketComplete = (assignment) => {
    if (!assignment?.company) return false;
    if (!assignment?.coreBrandGroups?.length) return false;
    if (competitorsRequired && !assignment?.competitors?.length) return false;
    return true;
  };

  const getCompletionStatus = (regionMarkets) => {
    const total = regionMarkets.length;
    const completed = regionMarkets.filter(({ regionId, market }) =>
      isMarketComplete(marketAssignments[`${regionId}|${market}`])
    ).length;

    return { completed, total };
  };

  return (
    <Card
      title="Per-Market Assignments"
      style={{ marginTop: 24 }}
    >
      <Text
        type="secondary"
        style={{ display: "block", marginBottom: 12 }}
      >
        ⚠️ Configure assignments for each market: a Company and at least one
        Core Brand group
        {competitorsRequired ? ", plus at least one Competitor," : ""} must be
        completed.
      </Text>

      {pairs.length === 0 ? (
        <div style={{ textAlign: "center", padding: 40, color: "#999" }}>
          Add regions & markets in Step 1 first.
        </div>
      ) : (
        <div style={{ maxHeight: 450, overflowY: "auto" }}>
          <Collapse
            size="small"
            ghost
            expandIconPosition="end"
          >
            {Object.entries(groupedPairs).map(
              ([regionId, { regionName, markets }]) => {
                const { completed, total } = getCompletionStatus(markets);

                return (
                  <Panel
                    key={regionId}
                    header={
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          width: "100%",
                        }}
                      >
                        <Text strong>{regionName}</Text>
                        <Space>
                          <Badge
                            count={`${completed}/${total}`}
                            style={{
                              backgroundColor:
                                completed === total ? "#52c41a" : "#faad14",
                              fontSize: "11px",
                            }}
                          />
                          <Text
                            type="secondary"
                            style={{ fontSize: 12 }}
                          >
                            {markets
                              .map((m) => m.market.toUpperCase())
                              .join(", ")}
                          </Text>
                        </Space>
                      </div>
                    }
                    style={{ marginBottom: 8 }}
                  >
                    <div style={{ paddingLeft: 16 }}>
                      {markets.map(({ regionId, market }) => {
                        const ck = `${regionId}|${market}`;
                        const a = marketAssignments[ck] || {
                          coreBrandGroups: [],
                          company: null,
                          competitors: [],
                        };

                        const handleCoreBrandDraftChange = (vals) => {
                          setCoreBrandDraftSelections((p) => ({
                            ...p,
                            [ck]: vals,
                          }));
                        };
                        const handleCoreBrandNameChange = (e) => {
                          setCoreBrandDraftName((p) => ({
                            ...p,
                            [ck]: e.target.value,
                          }));
                        };
                        const handleAddGroup = () => addGroup(ck);
                        const handleCompanyChange = (v) =>
                          setCompany(ck, v || null);

                        const isComplete = isMarketComplete(a);

                        return (
                          <Card
                            key={ck}
                            size="small"
                            type="inner"
                            style={{
                              marginBottom: 12,
                              border: isComplete
                                ? "2px solid #52c41a"
                                : "2px solid #ff4d4f",
                            }}
                            title={
                              <Space>
                                <Text
                                  strong
                                  style={{ fontSize: 13 }}
                                >
                                  {market.toUpperCase()}
                                </Text>
                                <Badge
                                  status={isComplete ? "success" : "error"}
                                  text={
                                    isComplete
                                      ? `Complete (${
                                          a.coreBrandGroups?.length || 0
                                        } core brands)`
                                      : "Incomplete"
                                  }
                                />
                              </Space>
                            }
                            extra={
                              isComplete && pairs.length > 1 ? (
                                <Button
                                  size="small"
                                  type="primary"
                                  ghost
                                  onClick={() => applyToAllMarkets(ck)}
                                >
                                  Apply to All Markets
                                </Button>
                              ) : null
                            }
                          >
                            {/* Company Selection */}
                            <div style={{ marginBottom: 12 }}>
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 8,
                                  marginBottom: 6,
                                }}
                              >
                                <Text
                                  strong
                                  style={{ fontSize: 12, minWidth: 80 }}
                                >
                                  Company *
                                </Text>
                                <Select
                                  allowClear
                                  style={{ flex: 1, maxWidth: 200 }}
                                  placeholder={
                                    skuCompaniesLoading
                                      ? "Loading companies..."
                                      : "Select company"
                                  }
                                  value={a.company || undefined}
                                  onChange={handleCompanyChange}
                                  loading={skuCompaniesLoading}
                                  options={companies.map((c) => ({
                                    value: c,
                                    label: c,
                                  }))}
                                />
                              </div>
                              {!a.company && (
                                <Text
                                  type="secondary"
                                  style={{ fontSize: 11 }}
                                >
                                  Select a company to enable core brands and
                                  competitors
                                </Text>
                              )}
                            </div>

                            {/* Core Brand Section */}
                            <div style={{ marginBottom: 12 }}>
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 8,
                                  marginBottom: 6,
                                }}
                              >
                                <Text
                                  strong
                                  style={{
                                    fontSize: 12,
                                    minWidth: 80,
                                    color: a.company ? "inherit" : "#d9d9d9",
                                  }}
                                >
                                  Core Brands *
                                </Text>
                                <div
                                  style={{ display: "flex", gap: 8, flex: 1 }}
                                >
                                  <TreeSelect
                                    multiple
                                    treeCheckable
                                    style={{ flex: 1 }}
                                    placeholder={
                                      companyBrandsLoading
                                        ? "Loading brands..."
                                        : a.company
                                        ? "Select brands/subbrands"
                                        : "Select company first"
                                    }
                                    treeData={getBrandsTreeData(a.company)}
                                    value={coreBrandDraftSelections[ck]}
                                    onChange={handleCoreBrandDraftChange}
                                    maxTagCount={3}
                                    allowClear
                                    showSearch
                                    disabled={!a.company}
                                    loading={companyBrandsLoading}
                                    treeDefaultExpandAll={false}
                                    showCheckedStrategy={TreeSelect.SHOW_PARENT}
                                  />
                                  <Input
                                    placeholder="Core brand name"
                                    style={{ width: 120 }}
                                    value={coreBrandDraftName[ck] || ""}
                                    onChange={handleCoreBrandNameChange}
                                    disabled={!a.company}
                                  />
                                  <Button
                                    size="small"
                                    type="dashed"
                                    disabled={
                                      !a.company ||
                                      a.coreBrandGroups.length >= 8
                                    }
                                    onClick={handleAddGroup}
                                  >
                                    Add
                                  </Button>
                                </div>
                              </div>

                              {/* Core Brand Groups Table */}
                              {a.coreBrandGroups.length === 0 && a.company && (
                                <div
                                  style={{
                                    marginTop: 8,
                                    padding: "8px 12px",
                                    backgroundColor: "#fff2f0",
                                    border: "1px solid #ffccc7",
                                    borderRadius: "4px",
                                  }}
                                >
                                  <Text
                                    style={{ fontSize: 11, color: "#ff4d4f" }}
                                  >
                                    ⚠️ At least one Core Brand group is required
                                    to proceed
                                  </Text>
                                </div>
                              )}
                              {a.coreBrandGroups.length > 0 && (
                                <div style={{ marginTop: 8, fontSize: 11 }}>
                                  <table
                                    style={{
                                      width: "100%",
                                      borderCollapse: "collapse",
                                    }}
                                  >
                                    <thead>
                                      <tr style={{ background: "#fafafa" }}>
                                        <th
                                          style={{
                                            padding: "4px 8px",
                                            border: "1px solid #f0f0f0",
                                            textAlign: "left",
                                          }}
                                        >
                                          Core Brand Name
                                        </th>
                                        <th
                                          style={{
                                            padding: "4px 8px",
                                            border: "1px solid #f0f0f0",
                                            textAlign: "left",
                                          }}
                                        >
                                          Brands
                                        </th>
                                        <th
                                          style={{
                                            padding: "4px 8px",
                                            border: "1px solid #f0f0f0",
                                            width: 60,
                                          }}
                                        >
                                          Action
                                        </th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {a.coreBrandGroups.map((g) => (
                                        <tr key={g.id}>
                                          <td
                                            style={{
                                              padding: "4px 8px",
                                              border: "1px solid #f0f0f0",
                                            }}
                                          >
                                            {g.name}
                                          </td>
                                          <td
                                            style={{
                                              padding: "4px 8px",
                                              border: "1px solid #f0f0f0",
                                            }}
                                          >
                                            {g.items
                                              .map((it) =>
                                                it.type === "brand"
                                                  ? it.brand
                                                  : `${it.brand}/${it.subbrand}`
                                              )
                                              .join(", ")}
                                          </td>
                                          <td
                                            style={{
                                              padding: "4px 8px",
                                              border: "1px solid #f0f0f0",
                                              textAlign: "center",
                                            }}
                                          >
                                            <Button
                                              size="small"
                                              danger
                                              type="link"
                                              onClick={() =>
                                                removeGroup(ck, g.id)
                                              }
                                              style={{
                                                padding: 0,
                                                fontSize: 11,
                                              }}
                                            >
                                              Delete
                                            </Button>
                                          </td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                              )}
                            </div>

                            {/* Competitors Section */}
                            <div>
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 8,
                                  marginBottom: 6,
                                }}
                              >
                                <Text
                                  strong
                                  style={{
                                    fontSize: 12,
                                    minWidth: 80,
                                    color: a.company ? "inherit" : "#d9d9d9",
                                  }}
                                >
                                  Competitors{competitorsRequired ? " *" : ""}
                                </Text>
                              </div>
                              <div style={{ marginBottom: 8 }}>
                                <TreeSelect
                                  multiple
                                  treeCheckable
                                  style={{ width: "100%" }}
                                  placeholder={
                                    getCompetitorsLoading(ck)
                                      ? "Loading competitors..."
                                      : a.company
                                      ? "Select competitors (companies or brands)..."
                                      : "Select company first"
                                  }
                                  treeData={getCompetitorsOptions(ck)}
                                  value={a.competitors?.map((c) =>
                                    c.brand
                                      ? `${c.competitor}|${c.brand}`
                                      : c.competitor
                                  )}
                                  onChange={(values) => {
                                    const competitorsList = values.map(
                                      (val) => {
                                        if (val.includes("|")) {
                                          // Brand selection: company|brandName format
                                          const [company, brandName] =
                                            val.split("|");
                                          return {
                                            competitor: company,
                                            brand: brandName,
                                          };
                                        } else {
                                          // Company selection
                                          return {
                                            competitor: val,
                                          };
                                        }
                                      }
                                    );
                                    setCompetitors(ck, competitorsList);
                                  }}
                                  maxTagCount={5}
                                  allowClear
                                  showSearch
                                  disabled={!a.company}
                                  loading={getCompetitorsLoading(ck)}
                                  treeDefaultExpandAll={false}
                                  showCheckedStrategy={TreeSelect.SHOW_PARENT}
                                />
                              </div>
                              {a.competitors?.length > 0 && (
                                <div style={{ fontSize: 11, color: "#666" }}>
                                  Selected: {a.competitors.length} competitor
                                  {a.competitors.length > 1 ? "s" : ""}
                                </div>
                              )}
                            </div>
                          </Card>
                        );
                      })}
                    </div>
                  </Panel>
                );
              }
            )}
          </Collapse>
        </div>
      )}
    </Card>
  );
};

export default Step3;
